<template>
  <div class="reset-form__container">
    <Form v-model:form-ref="formRef" class="reset-form__form" @submit="onFinish">
      <FormField v-if="!isGlobalVersion()" v-model="formState.mobile" name="mobile">
        <InputField :height="44" :placeholder="$t('shou-ji-hao')" class="reset-form__input" />
      </FormField>
      <FormField v-else v-model="formState.email" name="email">
        <InputField :height="44" :placeholder="$t('you-xiang')" class="reset-form__input" />
      </FormField>
      <FormField v-model="formState.sms_code" name="sms_code">
        <CaptchaInput
          v-model="formState.sms_code"
          :height="44"
          :placeholder="
            isGlobalVersion() ? $t('you-xiang-yan-zheng-ma') : $t('shou-ji-yan-zheng-ma')
          "
          :is-send="isSend"
          :times="times"
          input-class="reset-form__input reset-form__captcha-input"
          button-class="reset-form__captcha-button"
          @send-code="handleSendCode"
        >
          <template #button-text>
            {{ isSend ? $t('zhong-xin-huo-qu-times-s', [times]) : $t('huo-qu-yan-zheng-ma') }}
          </template>
        </CaptchaInput>
      </FormField>
      <FormField
        v-model="formState.password"
        name="password"
        no-errors
        :validate-on-blur="false"
        :validate-on-model-update="false"
        :validate-on-change="false"
      >
        <InputField
          type="password"
          :height="44"
          :placeholder="$t('xin-mi-ma')"
          show-password-toggle
          class="reset-form__input reset-form__input--password"
        />
        <template #description>
          <div class="reset-form__rule-tips">
            <div
              :class="{
                'reset-form__rule-tip': true,
                'reset-form__rule-tip--success': PwdValidator.isSafeRange(formState.password),
              }"
            >
              <component
                :is="PwdValidator.isSafeRange(formState.password) ? CheckIcon : CloseSmallIcon"
                class="reset-form__rule-tip-icon"
              />
              <Text variant="14" color="t7">
                {{ $t('chang-du-wei-620-ge-zi-fu') }}
              </Text>
            </div>
            <div
              :class="{
                'reset-form__rule-tip': true,
                'reset-form__rule-tip--success': PwdValidator.isValidFormat(formState.password),
              }"
            >
              <component
                :is="PwdValidator.isValidFormat(formState.password) ? CheckIcon : CloseSmallIcon"
                class="reset-form__rule-tip-icon"
              />
              <Text variant="14" color="t7">
                {{ $t('bao-han-shu-zi-da-xie-zi-mu-xiao-xie-zi-fu-zi-fu-san-zhong-ji-yi-shang') }}
              </Text>
            </div>
          </div>
        </template>
      </FormField>
      <FormField v-model="formState.checkPassword" name="checkPassword">
        <InputField
          type="password"
          :height="44"
          :placeholder="$t('que-ren-mi-ma')"
          show-password-toggle
          autocomplete="new-password"
          class="reset-form__input"
        />
      </FormField>
      <div class="reset-form__button-wrapper">
        <Button
          :disabled="isDisabled"
          :loading="isLoading"
          variant="purple"
          type="submit"
          class="reset-form__button"
        >
          {{ $t('que-ren') }}
        </Button>
      </div>
    </Form>
  </div>
</template>

<script lang="ts" setup>
import {
  changePassword,
  changePasswordEmail,
  changePasswordLogin,
  getEmailCode,
  getRegisterCode,
} from '@/api';
import { useSendCode } from '@/hooks/useSendCode';
import { phoneReg } from '@/utils/form';
import { message } from '@rendora/common/composables/message';
import CloseSmallIcon from '@rendora/design/assets/circle-error.svg?component';
import CheckIcon from '@rendora/design/assets/circle-success.svg?component';
import { Button } from '@rendora/design/components/button';
import { Form, FormField } from '@rendora/design/components/form';
import { InputField } from '@rendora/design/components/input';
import { Text } from '@rendora/design/components/text';
import CaptchaInput from '@rendora/design/widgets/captcha-input/index.vue';
import { useI18n } from 'vue-i18n';
import { isGlobalVersion } from '~/utils/area';
import { PwdValidator, transformPublicKey } from '~/utils/form';
import { emailReg } from '~/utils/regExp';
import { TRACK_EVENT, trackEvent } from '~/utils/track';

const { t: $t } = useI18n();
const { sendCode, isSend, times } = useSendCode();

// 类型定义
interface FormState {
  mobile: string;
  sms_code: string;
  password: string;
  checkPassword: string;
  email: string;
}

interface FormValues {
  mobile?: string;
  email?: string;
  sms_code: string;
  password: string;
  checkPassword: string;
}

type CheckParams =
  | {
      email: string;
      password: string;
      verify_code: string;
    }
  | {
      password1: string;
      password2: string;
    };

type ApiParams =
  | {
      rsa_key_id: number;
      auth_data: string;
    }
  | {
      rsa_key_id: number;
      auth_data: string;
      mobile: string;
      sms_code: string;
    };

// Props 和 Emits
defineProps<{
  noLabel?: boolean;
  size?: string;
  xLarge?: boolean;
  layout?: 'horizontal' | 'vertical' | 'inline';
}>();

const emits = defineEmits<{
  cancel: [];
  success: [];
}>();

// 响应式数据
const userInfo = useUserInfo();
const isLoading = ref(false);
const formRef = ref();

const formState = reactive<FormState>({
  mobile: userInfo.value?.phone_number || '',
  sms_code: '',
  password: '',
  checkPassword: '',
  email: userInfo.value?.email_address || '',
});

// 计算属性
const isDisabled = computed(() => {
  return !formState.password || !formState.checkPassword || !formState.sms_code;
});

// 验证函数
const validateEmailFormat = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validateMobileFormat = (mobile: string): boolean => {
  const mobileRegex = /^1[3-9]\d{9}$/;
  return mobileRegex.test(mobile);
};

// 生命周期
onBeforeUnmount(() => {
  clearData();
});

// 工具函数
const clearData = () => {
  formState.sms_code = '';
  formState.password = '';
  formState.checkPassword = '';
};

// 发送验证码
const handleSendCode = async () => {
  if (isSend.value) return;

  const contact = isGlobalVersion() ? formState.email : formState.mobile;

  if (!isGlobalVersion()) {
    if (!contact || !phoneReg.test(contact)) {
      message.warning($t('qing-shu-ru-zheng-que-de-shou-ji-hao'));
      return;
    }

    const res = await getRegisterCode({
      mobile: contact,
    });

    if (!res.code) {
      sendCode();
      message.success($t('yan-zheng-ma-fa-song-cheng-gong'));
    }
  } else {
    if (!contact || !emailReg.test(contact)) {
      message.warning($t('qing-shu-ru-zheng-que-de-you-xiang'));
      return;
    }

    const res = await getEmailCode({
      email: contact,
    });

    if (!res.code) {
      sendCode();
      message.success($t('yan-zheng-ma-fa-song-cheng-gong'));
    }
  }
};

// 修改密码
const onFinish = async (values?: any) => {
  // 使用传入的值或 formState
  const formValues = values || (formState as FormValues);
  // 手动验证表单
  const validationErrors: string[] = [];

  // 验证手机号/邮箱
  if (isGlobalVersion()) {
    if (!formValues.email) {
      validationErrors.push($t('qing-shu-ru-you-xiang'));
    } else if (!validateEmailFormat(formValues.email)) {
      validationErrors.push($t('you-xiang-ge-shi-bu-zheng-que'));
    }
  } else {
    if (!formValues.mobile) {
      validationErrors.push($t('qing-shu-ru-shou-ji-hao'));
    } else if (!validateMobileFormat(formValues.mobile)) {
      validationErrors.push($t('shou-ji-hao-ge-shi-bu-zheng-que'));
    }
  }

  // 验证验证码
  if (!formValues.sms_code) {
    validationErrors.push($t('qing-shu-ru-yan-zheng-ma'));
  }

  // 验证密码
  if (!formValues.password) {
    validationErrors.push($t('qing-shu-ru-mi-ma'));
  } else if (!PwdValidator.isSafeRange(formValues.password)) {
    validationErrors.push($t('mi-ma-chang-du-bu-fu-he-yao-qiu'));
  } else if (!PwdValidator.isValidFormat(formValues.password)) {
    validationErrors.push($t('mi-ma-ge-shi-bu-fu-he-yao-qiu'));
  }

  // 验证确认密码
  if (!formValues.checkPassword) {
    validationErrors.push($t('qing-que-ren-mi-ma'));
  } else if (formValues.checkPassword !== formValues.password) {
    validationErrors.push($t('liang-ci-mi-ma-shu-ru-bu-yi-zhi-qing-zhong-xin-shu-ru'));
  }

  // 如果有验证错误，显示第一个错误信息并返回
  if (validationErrors.length > 0) {
    message.error(validationErrors[0]);
    return;
  }

  if (isDisabled.value) {
    return;
  }

  isLoading.value = true;

  try {
    const checkParams: CheckParams = isGlobalVersion()
      ? {
          email: formValues.email!,
          password: formValues.password,
          verify_code: formValues.sms_code,
        }
      : {
          password2: formValues.checkPassword,
          password1: formValues.password,
        };

    const [encrypted, rsa] = await transformPublicKey(checkParams);

    let action;
    if (isGlobalVersion()) {
      action = changePasswordEmail;
    } else if (userInfo.value?.user_id) {
      action = changePasswordLogin;
    } else {
      action = changePassword;
    }

    const params: ApiParams = isGlobalVersion()
      ? {
          rsa_key_id: rsa.id,
          auth_data: encrypted,
        }
      : {
          mobile: formValues.mobile!,
          sms_code: formValues.sms_code,
          rsa_key_id: rsa.id,
          auth_data: encrypted,
        };

    await action(params as any);

    clearData();
    message.success($t('xiu-gai-mi-ma-cheng-gong'));
    trackEvent(TRACK_EVENT.PASSWORD_CHANGE_SUCCESS);

    // 登录后修改
    if (userInfo.value?.user_id) {
      emits('cancel');
    } else {
      emits('success');
    }
  } catch (error) {
    console.error('Password change failed:', error);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.reset-form__container {
  width: 328px;
  margin: 0 auto;
}

.reset-form__form {
  width: 100%;

  :deep(.form-item) {
    width: 100%;
    margin-bottom: 16px;
  }
}

.reset-form__input {
  letter-spacing: 0.3px;
  width: 100%;
}

.reset-form__input--password {
  margin-bottom: 10px;
}

.reset-form__captcha-input {
  flex: 1;
  padding-right: 120px;
}

.reset-form__captcha-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  z-index: 1;
  padding: 0 8px;
}

.reset-form__rule-tips {
  margin-top: 0px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.reset-form__rule-tip {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: var(--x-text-14-size);
  gap: 4px;
}

.reset-form__rule-tip-icon {
  width: 16px;
  height: 16px;
  font-size: 16px;
  flex-shrink: 0;
  color: var(--x-error-6);
}

.reset-form__rule-tip--success {
  .reset-form__rule-tip-icon {
    color: var(--x-success-6);
  }
}

.reset-form__button-wrapper {
  margin-top: 48px;
}

.reset-form__button {
  width: 100%;
  height: 46px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  line-height: 24px;
}
</style>
